import { Button, Fab, Popover, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import { LabelLinkTemplateMapper } from './LabelLinkTemplateMapper';
import useTemplateGroups from '../Providers/TemplateGroupsProvider';
interface Props {
  coordinate: string;
}

export const LabelLinkManager = ({ coordinate }: Props): React.JSX.Element => {
  const { templateGroupNames, setTemplateGroup, removeTemplateGroup } = useTemplateGroups();

  const [groupNameInput, setGroupNameInput] = useState<string>('');

  const [addPopoverAnchor, setAddPopoverAnchor] = useState<HTMLButtonElement>();
  const [currentGroup, setCurrentGroup] = useState<string>();
  const currentTab = useMemo(() => {
    if (templateGroupNames.length === 0) {
      return undefined;
    }

    if (currentGroup === undefined) {
      return 0;
    }

    const grpIndex = templateGroupNames.indexOf(currentGroup);
    if (grpIndex >= 0) {
      return grpIndex;
    }
    return undefined;
  }, [templateGroupNames, currentGroup]);

  // Auto-select the first group when groups exist but none is selected.
  // Step validation (enableNext) is owned by the parent LinkProfilePanel.
  useEffect(() => {
    if (templateGroupNames.length > 0 && currentGroup === undefined) {
      setCurrentGroup(templateGroupNames[0]);
    }
  }, [templateGroupNames, currentGroup]);

  const groupControl = useMemo(
    () => (
      <>
        <Popover
          open={addPopoverAnchor !== undefined}
          anchorEl={addPopoverAnchor}
          onAbort={() => {
            setGroupNameInput('');
            setAddPopoverAnchor(undefined);
          }}
        >
          <Stack direction={'column'} alignItems={'center'} margin={'8px'}>
            <TextField
              label="Group Name"
              value={groupNameInput}
              name="groupName"
              variant="outlined"
              onChange={(e) => setGroupNameInput(String(e.target.value))}
            />
            <Button
              fullWidth
              color={'secondary'}
              onClick={() => {
                setTemplateGroup(groupNameInput, {});
                setGroupNameInput('');
                setAddPopoverAnchor(undefined);
                if (currentGroup === undefined) {
                  setCurrentGroup(groupNameInput);
                }
              }}
              disabled={groupNameInput.trim().length < 1}
            >
              add
            </Button>
          </Stack>
        </Popover>
        <Stack direction={'row'} spacing={1}>
          <Tooltip title={`Remove ${currentGroup}`} placement={'top'}>
            <div>
              <Fab
                color={'error'}
                size={'small'}
                onClick={() => {
                  if (currentGroup) {
                    removeTemplateGroup(currentGroup);

                    if (currentTab === 0) {
                      setCurrentGroup(templateGroupNames[1] ?? undefined);
                    } else {
                      setCurrentGroup(templateGroupNames[currentTab! - 1] ?? undefined);
                    }
                  }
                }}
                disabled={templateGroupNames.length < 1}
              >
                <Delete />
              </Fab>
            </div>
          </Tooltip>
          <Tooltip title={`Add Group`} placement={'top'}>
            <div>
              <Fab color={'secondary'} size={'small'} onClick={(e) => setAddPopoverAnchor(e.currentTarget)}>
                <Add />
              </Fab>
            </div>
          </Tooltip>
        </Stack>
      </>
    ),
    [addPopoverAnchor, currentGroup, currentTab, groupNameInput, templateGroupNames],
  );

  return (
    <Stack spacing={1}>
      <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'flex-start'}>
        {groupControl}
        {currentTab !== undefined && (
          <Tabs
            value={currentTab}
            variant={'scrollable'}
            scrollButtons={'auto'}
            onChange={(_, newTab) => setCurrentGroup(templateGroupNames[newTab])}
          >
            {templateGroupNames.map((tabName: string) => (
              <Tab key={`tGroup-${tabName}`} label={<Typography color={'textPrimary'}>{tabName}</Typography>} />
            ))}
          </Tabs>
        )}
      </Stack>
      {currentGroup !== undefined && (
        <LabelLinkTemplateMapper coordinate={coordinate} groupName={currentGroup} allowAddingPages={true} />
      )}
    </Stack>
  );
};
